import { Search, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import SocioSearch from "../../../common/SocioSearch";
import QuotasSection from "../QuotasSection";
import { motion } from "framer-motion";
import { getActiveQuotas, getPaymentsByRut, getQuotas } from "../../../../services/quotasService";
import { PageLoader } from "../../../loader";
import PaymentHistory from "../AuxQuotas/PaymentHistory";
import AddPayment from "../AuxQuotas/AddPayment";
import CreateManualQuota from "./CreateManualQuota";

const ViewSocioAdmin = () => {
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [ quotas, setQuotas ] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedQuotas, setSelectedQuotas] = useState([]);
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [quotaMessage, setQuotaMessage] = useState("");
  const [showCreateManualQuota, setShowCreateManualQuota] = useState(false);

 const loadQuotas = async (year) => {
  try {
    setLoading(true);
    setQuotaMessage("");
    setQuotas([]);

    const currentYear = year || new Date().getFullYear();
    const response = await getActiveQuotas(selectedSocio.rut, currentYear);
    if (response?.message) {
      setQuotaMessage(response.message);
      return;
    }

    if (Array.isArray(response) && response.length === 0) {
      setQuotaMessage("Este socio no tiene cuotas activas pendientes.");
      return;
    }

    setQuotas(response);

  } catch (error) {
    console.error("Error loading quotas:", error);
    setQuotaMessage("Ocurrió un error al cargar las cuotas.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (selectedSocio) {
      loadQuotas();
    }
  }
  , [selectedSocio, setLoading]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await getPaymentsByRut(selectedSocio.rut);
      if (response.error) {
        console.error("Error fetching payments:", response.message);
        return;
      }
      setPayments(response);
    } catch (error) {
      console.error("Error loading payments:", error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (showPaymentHistory) {
      loadPayments();
    }
  }, [showPaymentHistory, selectedSocio]); 



  const handleViewPaymentHistory = () => {
    setShowAddPayment(false);
    setShowPaymentHistory(true);
  }

  const handleViewAddPayment = (selectedQuotas, calculatedTotal) => {
    setSelectedQuotas(selectedQuotas);
    setCalculatedTotal(calculatedTotal);
    setShowPaymentHistory(false);
    setShowAddPayment(true);
  }

  const handleBackToQuotas = () =>{
    setShowAddPayment(false);
    setShowPaymentHistory(false);
    setSelectedQuotas([]);
    setCalculatedTotal(0);
    if (showPaymentHistory) {
      setPayments([]);
    }
  };

  const handleSavePayment = () => {
    setShowAddPayment(false);
    setShowPaymentHistory(false);
    setSelectedQuotas([]);
    setCalculatedTotal(0);
    loadQuotas();
  };

  const handleQuotaPaymentRemoved = async () => {
    // Recargar cuotas y pagos después de eliminar el pago de una cuota
    await loadQuotas();
    if (showPaymentHistory) {
      await loadPayments();
    }
  };

  if (loading) return <PageLoader message="Cargando cuotas del socio..." />;

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      {!showPaymentHistory && !showAddPayment && (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-r from-[#0488AF] to-[#C42251] rounded-lg">
            <Search className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Buscar Socio</h2>
            <p className="text-gray-600">Selecciona un socio para ver sus cuotas</p>
          </div>
        </div>

      {/* Barra de búsqueda de socios */}        
      <SocioSearch
          onSocioSelect={setSelectedSocio}
          selectedSocio={selectedSocio}
          setLoading={setLoading}
        />
      </div>
      )}

    {/* Información del socio seleccionado */}
    {selectedSocio && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <span className="loader border-4 border-blue-500 border-t-transparent rounded-full w-10 h-10 animate-spin"></span>
          </div>
        ) : quotaMessage ? (
          <div className="max-w-xl mx-auto bg-blue-50 border border-blue-200 text-blue-800 rounded-xl p-6 mt-6 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
                <Search className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold mb-1">Información:</h4>
                <p className="text-sm">{quotaMessage}</p>
              </div>
            </div>
            {selectedSocio && (
              <button
                onClick={() => setShowCreateManualQuota(true)}
                className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-[#0488AF] to-[#C42251] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Crear Cuota Manual
              </button>
            )}
          </div>
        ) : showPaymentHistory ? (
          <PaymentHistory 
            isOpen={showPaymentHistory} 
            onClose={() => setShowPaymentHistory(false)} 
            payments={payments} 
            socio={selectedSocio} 
            onBack={handleBackToQuotas}
          />
        ) : showAddPayment ? (
          <AddPayment 
            selectedQuotas={selectedQuotas} 
            calculatedTotal={calculatedTotal} 
            onBack={handleBackToQuotas} 
            socio={selectedSocio} 
            onSavePayment={handleSavePayment} 
          />
        ) : (
          quotas.length > 0 && (
            <QuotasSection 
              selectedSocio={selectedSocio} 
              quotas={quotas} 
              onViewPaymentHistory={handleViewPaymentHistory} 
              onViewAddPayment={handleViewAddPayment} 
              onClose={() => setSelectedSocio(null)} 
              reloadQuotas={handleQuotaPaymentRemoved}
            />
          )
        )}
      </motion.div>
    )}

      {/* Estado inicial */}
      {!selectedSocio && (
        <div className="text-center py-12">
          <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <Search className="text-gray-400" size={32} />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Selecciona un Socio</h3>
          <p className="text-gray-600">Usa la barra de búsqueda para encontrar y seleccionar un socio</p>
        </div>
      )}

      {/* Modal para crear cuota manual */}
      {selectedSocio && (
        <CreateManualQuota
          isOpen={showCreateManualQuota}
          onClose={() => setShowCreateManualQuota(false)}
          socio={selectedSocio}
          onSuccess={() => {
            loadQuotas();
            setShowCreateManualQuota(false);
          }}
        />
      )}
    </div>
  );
};

export default ViewSocioAdmin;